import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import zxcvbn from "zxcvbn";
import { api } from "@/lib/api";
import logo from "@/assets/prydes-logo.png";
import BrandLogo from "@/components/BrandLogo";

const CRIMSON = "#bf3b4f";
const GREEN = "#00c86e";
const MIN_SCORE = 3; // minimum password strength score
const SCORE_LABELS = ["Too guessable", "Very guessable", "Somewhat guessable", "Safely unguessable", "Very unguessable"];
const SCORE_COLORS = ["#dc2626", "#f97316", "#f59e0b", "#00c86e", "#059669"];
const INVALID_COPY = {
  expired: "This link has expired. Set-password links are valid for a limited time — please request a new one from the sign-in page.",
  used: "This link has already been used. If you still need to change your password, request a new link from the sign-in page.",
  invalid: "This link isn't valid. Please request a new one from the sign-in page.",
  missing: "This reset link is missing or malformed. Please request a new one from the sign-in page.",
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [checking, setChecking] = useState(true);
  const [invalidReason, setInvalidReason] = useState(token ? "" : "missing");

  // Validate the token on load so an expired/used link shows a clear message
  // instead of rendering the password form for any token string.
  useEffect(() => {
    if (!token) { setChecking(false); return; }
    let live = true;
    api.validateResetToken(token)
      .then((r) => { if (live) setInvalidReason(r.valid ? "" : (r.reason || "invalid")); })
      .catch(() => { if (live) setInvalidReason("invalid"); })
      .finally(() => { if (live) setChecking(false); });
    return () => { live = false; };
  }, [token]);

  const strength = useMemo(() => (pwd ? zxcvbn(pwd) : null), [pwd]);
  const score = strength ? strength.score : 0;
  const strongEnough = !!pwd && score >= MIN_SCORE;

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("Password must be at least 8 characters.");
    if (!strongEnough) return toast.error(`Please choose a stronger password (needs "${SCORE_LABELS[MIN_SCORE]}" or better).`);
    if (pwd !== confirm) return toast.error("The two passwords don't match.");
    setLoading(true);
    try {
      const r = await api.resetPassword(token, pwd);
      setDone(true);
      toast.success(r.message || "Your password has been reset.");
    } catch (err) {
      setInvalidReason("expired");
      toast.error(err.response?.data?.detail || "This reset link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 font-body" style={{ backgroundColor: "#fcfcfc" }}>
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8"><BrandLogo fallback={logo} className="h-14 w-auto" /></div>

        {checking ? (
          <div className="flex flex-col items-center py-8" data-testid="reset-checking">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: CRIMSON }} />
            <p className="text-sm text-slate-500 mt-3">Checking your link…</p>
          </div>
        ) : invalidReason ? (
          <div className="text-center" data-testid="reset-no-token">
            <div className="mx-auto h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center mb-4"><AlertTriangle className="h-6 w-6 text-amber-500" /></div>
            <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight">{invalidReason === "used" ? "Link already used" : invalidReason === "expired" ? "Link expired" : "Link not valid"}</h2>
            <p className="text-sm text-slate-500 mt-2">{INVALID_COPY[invalidReason] || INVALID_COPY.invalid}</p>
            <Button onClick={() => nav("/login")} className="mt-6 h-11 w-full rounded-xl font-semibold" style={{ backgroundColor: CRIMSON }} data-testid="reset-go-login">Back to sign in</Button>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4"><CheckCircle2 className="h-6 w-6" style={{ color: GREEN }} /></div>
            <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight">Password updated</h2>
            <p className="text-sm text-slate-500 mt-2">You can now sign in with your new password.</p>
            <Button onClick={() => nav("/login")} className="mt-6 h-11 w-full rounded-xl font-semibold" style={{ backgroundColor: CRIMSON }} data-testid="reset-signin">Sign in</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div>
              <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><KeyRound className="h-5 w-5" style={{ color: CRIMSON }} /> Set a new password</h2>
              <p className="text-sm text-slate-500 mt-1.5">Choose a new password for your Prydes Support account.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">New password</Label>
              <Input data-testid="reset-password" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 8 characters" className="mt-1 h-11 text-sm" autoFocus required />
              {pwd && (
                <div className="mt-2" data-testid="password-strength">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-1.5 flex-1 rounded-full transition-colors"
                        style={{ backgroundColor: i <= score ? SCORE_COLORS[score] : "#e2e8f0" }} />
                    ))}
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="text-[11px] font-medium" style={{ color: SCORE_COLORS[score] }} data-testid="password-strength-label">
                      {SCORE_LABELS[score]}
                    </span>
                    {strongEnough
                      ? <span className="text-[11px] font-medium flex items-center gap-1" style={{ color: GREEN }}><CheckCircle2 className="h-3 w-3" /> Strong enough</span>
                      : <span className="text-[11px] text-slate-400">Needs "{SCORE_LABELS[MIN_SCORE]}" or better</span>}
                  </div>
                  {!strongEnough && strength?.feedback?.warning && (
                    <p className="mt-1 text-[11px] text-amber-600">{strength.feedback.warning}</p>
                  )}
                  {!strongEnough && (strength?.feedback?.suggestions || []).length > 0 && (
                    <ul className="mt-0.5 text-[11px] text-slate-500 list-disc list-inside space-y-0.5">
                      {strength.feedback.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Confirm new password</Label>
              <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter your password" className="mt-1 h-11 text-sm" required />
              {confirm && confirm !== pwd && <p className="mt-1 text-[11px] text-rose-500">Passwords don't match.</p>}
            </div>
            <Button data-testid="reset-submit" type="submit" disabled={loading || !strongEnough || pwd !== confirm} className="w-full h-11 text-sm font-semibold rounded-xl hover:opacity-95 disabled:opacity-50" style={{ backgroundColor: CRIMSON }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
